import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { store } from '../store';
import { USER_ROLES } from '../constants/constants';

// Define your color themes here
export const themes = {
  
  green: {
    primaryColor: '#315e45',
    highlighterColor: '#f9f3ee',
    lightShadowColor: '#9ad69C',
    sideNavigation: '#315e45',
    fontColor: 'white',
  }, 
  purple: {
    primaryColor: '#722ed1',
    highlighterColor: '#b37feb',
    lightShadowColor: '#f9f0ff',
    sideNavigation: '#722ed1',
    fontColor: '#fff',
  },
  blue: {
    primaryColor: '#1677ff',
    highlighterColor: '#bae0ff',
    lightShadowColor: '#e6f4ff',
    sideNavigation: '#1677ff',
    fontColor: '#fff',
  },
  orange: {
    primaryColor: '#de7f39',
    highlighterColor: '#ffc069',
    lightShadowColor: '#fff4ec',
    sideNavigation: '#de7f39',
    fontColor: '#fff',
  },
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [activeTheme, setActiveThemeState] = useState(() => {
    // Check localStorage for user's theme preference first
    const savedTheme = localStorage.getItem('selectedTheme');
    if (savedTheme && themes[savedTheme]) {
      return savedTheme;
    }
    return 'green';
  });

  // Function to determine active theme based on role/domain
  const getActiveTheme = useCallback(() => {
    const hostname = window.location.hostname.toLowerCase();
    
    // FOR LOCALHOST TESTING: Check query string for theme parameter
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const urlParams = new URLSearchParams(window.location.search);
      const themeParam = urlParams.get('theme');
      // If theme query parameter exists and is valid, use it
      if (themeParam && themes[themeParam]) {
        return themeParam;
      }
    }
    
    // Check if user has manually selected a theme
    const savedTheme = localStorage.getItem('selectedTheme');
    if (savedTheme && themes[savedTheme]) {
      return savedTheme;
    }
    
    const currentRole = store.getState().authorization?.role;
    const platform = store.getState().authorization;

    if (currentRole === USER_ROLES.ENTERPRISE_ADMIN ||
      hostname.includes('enterprise.medhaul.link') ||
      platform.isEnterprise) {
      return "green";
    }

    return 'green';
  }, []);

  // Custom setter that saves to localStorage
  const setActiveTheme = useCallback((newTheme) => {
    if (themes[newTheme]) {
      localStorage.setItem('selectedTheme', newTheme);
      setActiveThemeState(newTheme);
    }
  }, []);

  // Subscribe to store changes
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const newTheme = getActiveTheme();
      setActiveThemeState((prevTheme) => {
        if (newTheme !== prevTheme) return newTheme;
        return prevTheme;
      });
    });

    // Set initial theme
    const initialTheme = getActiveTheme();
    setActiveThemeState(initialTheme);

    // Cleanup subscription
    return () => unsubscribe();
  }, [getActiveTheme]);

  const themeVars = activeTheme ? themes[activeTheme] : null;

  useEffect(() => {
    if (themeVars) {
      document.documentElement.style.setProperty('--ant-primary-color', themeVars.primaryColor);
      document.documentElement.style.setProperty('--ant-higlighter-color', themeVars.highlighterColor);
      document.documentElement.style.setProperty('--ant-light-shadow-color', themeVars.lightShadowColor);
      document.documentElement.style.setProperty('--ant-sidebar-color', themeVars.sideNavigation);
      document.documentElement.style.setProperty('--ant-font-color', themeVars.fontColor);
    }
  }, [themeVars]);

  const value = {
    activeTheme,
    setActiveTheme,
    themeVars,
    themes,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
